// client/src/dnd/emitter_tool/spritesheetAtlas.js
import * as PIXI from "pixi.js";

const SPRITESHEET_JSON_URL = "/assets/emitter/spritesheet.json";
const SPRITES_DIR = "/assets/emitter/sprites";
const SPRITES_MANIFEST_URL = `${SPRITES_DIR}/sprites.json`;

let _atlas = null;
let _loading = null;

const isDev =
  typeof process !== "undefined" &&
  process.env &&
  (process.env.NODE_ENV === "development" || process.env.VITE_DEV_SERVER_URL);

function withBust(url) {
  if (!isDev) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}bust=${Date.now()}`;
}

async function tryFetchJSON(url) {
  try {
    const res = await fetch(withBust(url), { cache: "no-store" });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

async function tryLoadPackedSpritesheet() {
  const peek = await tryFetchJSON(SPRITESHEET_JSON_URL);
  if (!peek) return null;
  try {
    const sheet = await PIXI.Assets.load(withBust(SPRITESHEET_JSON_URL));
    const textures = sheet?.textures || {};
    const frameNames = Object.keys(textures);
    return { textures, frameNames };
  } catch {
    return null;
  }
}

async function tryLoadLoosePngsFromManifest() {
  const manifest = await tryFetchJSON(SPRITES_MANIFEST_URL);
  if (!manifest || !Array.isArray(manifest.sprites) || manifest.sprites.length === 0) {
    return null;
  }

  const textures = {};
  const frameNames = [];

  for (const entry of manifest.sprites) {
    if (!entry) continue;
    const file = String(entry.file || "").trim();
    if (!file) continue;

    const name = String(entry.name || "").trim() || file;
    const url = withBust(`${SPRITES_DIR}/${file}`);

    try {
      const tex = await PIXI.Assets.load(url);
      if (tex) {
        textures[name] = tex;
        frameNames.push(name);
      }
    } catch {}
  }

  return { textures, frameNames };
}

function mergeAtlases(primary, secondary) {
  const outTextures = { ...(primary?.textures || {}) };
  const outNamesSet = new Set(primary?.frameNames || []);

  const secTextures = secondary?.textures || {};
  const secNames = secondary?.frameNames || [];

  for (const n of secNames) {
    if (outTextures[n]) continue;
    outTextures[n] = secTextures[n];
    outNamesSet.add(n);
  }

  const frameNames = Array.from(outNamesSet);
  frameNames.sort((a, b) => a.localeCompare(b));
  return { textures: outTextures, frameNames };
}

export async function loadAtlas() {
  if (_atlas) return _atlas;
  if (_loading) return _loading;

  _loading = (async () => {
    const [packed, loose] = await Promise.all([
      tryLoadPackedSpritesheet(),
      tryLoadLoosePngsFromManifest(),
    ]);

    let atlas = null;

    if (packed && loose) {
      atlas = mergeAtlases(packed, loose);
    } else if (packed) {
      atlas = packed;
    } else if (loose) {
      atlas = loose;
    } else {
      atlas = { textures: {}, frameNames: [] };
    }

    _atlas = atlas;
    return _atlas;
  })();

  return _loading;
}

export function getAtlas() {
  return _atlas;
}

export function invalidateAtlasCache() {
  _atlas = null;
  _loading = null;
}

export async function purgeSpriteCaches() {
  if (_atlas && _atlas.textures) {
    for (const t of Object.values(_atlas.textures)) {
      try { t.destroy(true); } catch {}
    }
  }

  try { await PIXI.Assets.unload(withBust(SPRITESHEET_JSON_URL)); } catch {}

  try {
    const manifest = await tryFetchJSON(SPRITES_MANIFEST_URL);
    if (manifest && Array.isArray(manifest.sprites)) {
      for (const entry of manifest.sprites) {
        const file = String(entry.file || "").trim();
        if (!file) continue;
        try { await PIXI.Assets.unload(withBust(`${SPRITES_DIR}/${file}`)); } catch {}
      }
    }
  } catch {}

  try { PIXI.Assets.reset(); } catch {}

  invalidateAtlasCache();
}
